/*
  Feerway behaviour.
  1. Shrine's VariantRadios never updates the "Color <value>" legend, so the label kept the first variant after a
     change; this keeps each fieldset's [data-selected-value] in step with its checked input.
  2. Offer upkeep in the basket: a free gift (line property _fw_gift_for = product id) stays at quantity 1 and leaves
     the basket when no item of its product is left; gift products (product type "Gift") added any other way are
     removed. The "add your free second item" button adds one more of a line.
     Prices are Shopify's automatic discounts; this only keeps the basket tidy.
*/

// Deferred images (data-fw-defer): get their real src/srcset only after the page has loaded, and only when they come
// within reach of the screen, so nothing below the fold competes with the first product photo.
(() => {
  const reveal = (img) => {
    if (img.dataset.fwSrcset) img.srcset = img.dataset.fwSrcset;
    if (img.dataset.fwSrc) img.src = img.dataset.fwSrc;
    img.removeAttribute('data-fw-defer');
  };
  const start = () => {
    const images = [...document.querySelectorAll('img[data-fw-defer]')];
    if (!('IntersectionObserver' in window)) return images.forEach(reveal);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      }),
      { rootMargin: '600px 800px' }
    );
    images.forEach((img) => observer.observe(img));
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const fieldset = input.closest('variant-radios fieldset, variant-selects fieldset');
  const label = fieldset && fieldset.querySelector('[data-selected-value]');
  if (label) label.textContent = input.value;
});

(() => {
  const drawer = () => document.querySelector('cart-drawer');
  const onCartPage = () => Boolean(document.querySelector('cart-items:not(cart-drawer-items)'));

  const post = (url, body) => {
    const sections = drawer() && !onCartPage() ? drawer().getSectionsToRender().map((s) => s.id) : [];
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    }).then((response) => response.json());
  };

  const refresh = (state) => {
    if (onCartPage()) {
      window.location.reload();
    } else if (drawer() && state && state.sections) {
      drawer().renderContents(state);
      // renderContents always clears the empty state (Shrine); restore it like cart.js does.
      const empty = state.item_count === 0;
      drawer().classList.toggle('is-empty', empty);
      const inner = drawer().querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', empty);
      const items = drawer().querySelector('cart-drawer-items');
      if (items) items.classList.toggle('is-empty', empty);
    }
  };

  let busy = false;
  const tidyGifts = async () => {
    if (busy) return;
    busy = true;
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const isGift = (item) => Boolean((item.properties || {})._fw_gift_for) || item.product_type === 'Gift';
      const productIds = new Set(cart.items.filter((item) => !isGift(item)).map((item) => String(item.product_id)));
      const updates = {};
      const seen = new Set();
      cart.items.forEach((item) => {
        if (!isGift(item)) return;
        // A gift added any other way than through an offer card (no _fw_gift_for) is not tied to a purchase: remove it.
        const giftFor = (item.properties || {})._fw_gift_for;
        const keep = Boolean(giftFor) && productIds.has(String(giftFor)) && !seen.has(giftFor);
        seen.add(giftFor);
        const quantity = keep ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length === 0) return;
      let state;
      for (const [id, quantity] of Object.entries(updates)) {
        state = await post(`${window.routes.cart_change_url}`, { id, quantity });
      }
      refresh(state);
    } catch (error) {
      console.error(error);
    } finally {
      busy = false;
    }
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => tidyGifts());
  }
  if (onCartPage()) tidyGifts();

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-fw-add-variant]');
    if (!button) return;
    event.preventDefault();
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    try {
      const state = await post(`${window.routes.cart_add_url}`, { items: [{ id: Number(button.dataset.fwAddVariant), quantity: 1 }] });
      if (state.status) throw new Error(state.description);
      refresh(state);
    } catch (error) {
      console.error(error);
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
    }
  });
})();
